import { observable, computed, action, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { CursorPaged, type CursorPageQuery } from './paging'
import { isAbortError } from '@/utils/abort-util'
import { EMPTY, merge, mergeMap, type Observable, of } from 'rxjs'

export type FetchItems<T, F = Record<string, unknown>> = (options: {
  paging: CursorPageQuery
  signal: AbortSignal
  filter?: F
}) => Promise<CursorPaged<T>>

export interface InfiniteScrollProviderOptions<T, F> {
  fetchItems: FetchItems<T, F>
  limit: number
}

export interface FetchOptions<F> {
  filter?: F
  signal?: AbortSignal
  /**
   * If `true`, the `items` and `total` won't be reset immediately.
   * Useful when you want to show the current content while fetching the
   * new content.
   */
  staleWhileRevalidate?: boolean
}

export class InfiniteScrollProvider<T, F = Record<string, unknown>> {
  private _filter: F | undefined = undefined
  private _abortController: AbortController | null = null
  private _itemsSet = new Set<T>()

  private readonly _fetchItems: FetchItems<T, F>

  /**
   * Whether we have paged the entire data set.
   */
  @observable accessor isDone = false
  /**
   * When the scroller has already fetched its initial page, and {@link fetch}
   * is called with `staleWhileRevalidate=true`, this will be `true` while
   * that fetch is ongoing. This is used primarily to only render loading UI
   * for the initial fetch and not while revalidating.
   */
  @observable accessor isRevalidating = false
  /**
   * The current cursor.
   */
  @observable accessor cursor: string | null = null
  /**
   * How many to return at a time.
   */
  @observable accessor limit = 10
  /**
   * The total available records given the current filter.
   */
  @observable accessor total = 0
  /**
   * All the items collected.
   */
  @observable accessor _items = observable.array<T>()

  constructor({ fetchItems, limit }: InfiniteScrollProviderOptions<T, F>) {
    this.limit = limit
    this._fetchItems = fetchItems
    this.more = this.more.bind(this)
  }

  /**
   * The current items.
   */
  get items(): readonly T[] {
    return this._items
  }

  /**
   * Whether the infinite scroll check is active; that is, a query will fire
   * when triggered.
   */
  @computed
  get active() {
    return this.fetch.resolved && this.more.resolved && !this.isDone
  }

  /**
   * Resets the paging state.
   */
  @action.bound
  reset(staleWhileRevalidate = false) {
    this.isDone = false
    this.cursor = null

    this.isRevalidating = this._items.length > 0 && staleWhileRevalidate
    if (!staleWhileRevalidate) {
      this.total = 0
      this._itemsSet.clear()
      this._items.clear()
    }
  }

  /**
   * Adds an item to the end of the list if it is not already present.
   *
   * @return `true` if a new item was added, `false` otherwise.
   */
  @action.bound
  add(item: T): boolean {
    return this._pushCore(item)
  }

  /**
   * Adds an item to the beginning of the list if it is not already present.
   *
   * @return `true` if a new item was added, `false` otherwise.
   */
  prepend(item: T): boolean {
    return this._unshiftCore(item)
  }

  /**
   * Removes elements based on the predicate.
   */
  @action.bound
  removeWhere(predicate: (t: T) => boolean) {
    for (let i = this._items.length - 1; i >= 0; i--) {
      const item = this._items[i]
      if (predicate(item)) {
        this._items.splice(i, 1)
        this._itemsSet.delete(item)
      }
    }
  }

  /**
   * Removes the item.
   *
   * @param item
   * @return `true` if an item was removed, `false` otherwise.
   */
  @action.bound
  remove(item: T) {
    if (this._itemsSet.has(item)) {
      this._items.splice(this._items.indexOf(item), 1)
      this._itemsSet.delete(item)
      return true
    }

    return false
  }

  /**
   * Starts a new fetch from the beginning with the given filter.
   */
  fetch = task(
    async ({ filter, signal, staleWhileRevalidate }: FetchOptions<F> = {}) => {
      this.reset(staleWhileRevalidate)
      this._filter = filter
      const promise = this.more(signal)
      const ownSignal = this._abortController?.signal
      return promise.then(
        action((result) => {
          if (signal?.aborted || ownSignal?.aborted) {
            // Don't reset the revalidating flag when aborted.
            return result
          }

          this.isRevalidating = false
          return result
        }),
      )
    },
  )

  /**
   * Fetches the next page.
   */
  more = task.resolved(async (signal?: AbortSignal) => {
    signal = this._abortAndInitNewSignal(signal)
    return await this.invokeFetchItems(signal)
  })

  /**
   * Aborts the current signal and creates a new one that is merged with
   * the input signal if any is specified.
   *
   * @param signal
   * @private
   */
  private _abortAndInitNewSignal(signal: AbortSignal | undefined) {
    // Abort existing fetch if any.
    this._abortController?.abort()
    this._abortController = new AbortController()
    return signal
      ? AbortSignal.any([signal, this._abortController.signal])
      : this._abortController.signal
  }

  /**
   * Internal push method that adds to both the item list and the set, ensuring
   * no dupes are added.
   *
   * @param item
   * @private
   */
  private _pushCore(item: T) {
    if (this._itemsSet.has(item)) {
      return false
    }

    this._items.push(item)
    this._itemsSet.add(item)
    return true
  }

  /**
   * Internal push method that adds to both the item list and the set, ensuring
   * no dupes are added.
   *
   * @param item
   * @private
   */
  private _unshiftCore(item: T) {
    if (this._itemsSet.has(item)) {
      return false
    }

    this._items.unshift(item)
    this._itemsSet.add(item)
    return true
  }

  /**
   * Fetches the next page.
   *
   * @param signal
   * @private
   */
  private async invokeFetchItems(signal: AbortSignal): Promise<CursorPaged<T>> {
    const after = this.cursor ? this.cursor : undefined
    try {
      const result = await this._fetchItems({
        paging: {
          after: after,
          limit: this.limit,
        },
        signal,
        filter: this._filter,
      })

      signal.throwIfAborted()
      runInAction(() => {
        if (!after) {
          this._items.clear()
          this._itemsSet.clear()
        }

        for (const item of result.data) {
          this._pushCore(item)
        }

        this.cursor = result.next
        this.total = result.total ?? 0

        if (!result.next) {
          this.isDone = true
        } else if (
          // If the scroller has the exact number of items as
          // the data source advertises as the total, mark as done.
          typeof result.total === 'number' &&
          result.total === this._items.length
        ) {
          this.isDone = true
        }
      })
      return result
    } catch (err) {
      if (isAbortError(err) || signal.aborted) {
        return CursorPaged.empty()
      }
      throw err
    }
  }
}

export interface SyncInfiniteScrollerItemsOptions<T> {
  /**
   * The scroll provider to sync.
   */
  scroller: InfiniteScrollProvider<T>

  /**
   * Stream of removals to sync to the provider.
   */
  removals$?: Observable<T>

  /**
   * Stream of additions to sync to the provider.
   */
  additions$?: Observable<T>

  /**
   * The index to insert the items at when items are added.
   * Defaults to `'end'`.
   */
  insertAt?: 'start' | 'end'
}

/**
 * Creates an observable that, as a side effect, syncs additions and removals
 * of items with a scroll provider.
 */
export function syncInfiniteScrollerItems<T>({
  scroller,
  removals$ = EMPTY,
  additions$ = EMPTY,
  insertAt = 'end',
}: SyncInfiniteScrollerItemsOptions<T>) {
  return merge(
    additions$.pipe(
      mergeMap(
        action((item) => {
          if (insertAt === 'start') {
            if (!scroller.prepend(item)) {
              return EMPTY
            }
          } else {
            if (!scroller.add(item)) {
              return EMPTY
            }
          }
          return of({ type: 'add' as const, item })
        }),
      ),
    ),
    removals$.pipe(
      mergeMap(
        action((item) => {
          if (!scroller.remove(item)) {
            return EMPTY
          }

          return of({ type: 'remove' as const, item })
        }),
      ),
    ),
  )
}
